import { useNavigate, useNavigation, useRevalidator, useSearchParams, useLocation, useRouteLoaderData } from "@remix-run/react";
import { useEffect, useMemo, useRef, useState, Suspense, useCallback, lazy } from "react";
import { EmptyLazyFallback, loadLazyModule } from "~/lib/helpers/resolveLazyModule";
import Text from "~/components/atoms/Text";
import Breadcrumbs from "~/components/blocks/BreadCrumbs";
import PGridCard from "~/components/blocks/Card/PGridCard";
import { ClientOnly } from "~/components/blocks/ClientOnly/client-only";
import SearchBar from "./Searchbar";
import { Pagination, PaginationContent, PaginationItem, PaginationPrevious, PaginationNext } from "~/components/ui/custom-radix-components/pagination";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { ChevronDown } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Workspace, PropertyData } from "./interfaces";
import { filterOptions } from "./constants";
import {
  buildAreaPageListingHeadline,
  buildAreaPageListingResultText,
  buildAreaPageListingSubheadline,
  getAreaHeadlineBrowseContext,
  resolveAreaPageHeadlineSpaceType,
} from "~/utils/Meta/areaMeta";
import { buildGeoSearchPageListingSubheadline } from "~/utils/Meta/geoSearchMeta";
import { buildLocationGeosearchPageListingSubheadline } from "~/utils/Meta/locationGeosearchMeta";
import { buildingSlug, listingSlug, propertySlug, workspaceSlug } from "~/lib/helpers/slugHelper";
import NoResults from "./NoResult";
import { DropdownMenuCheckboxItem } from "~/components/ui/dropdown-menu";
import Container from "~/components/atoms/Container";
import { PAGE_SECTION_IDS, PAGE_SECTION_SCROLL_CLASS } from "~/lib/constants/pageSectionIds";
import AreaDesktopLayout from "~/components/blocks/AreaPage/DesktopLayout";
import PListCard from "~/components/blocks/Card/PListCard";
import {
  appendGeosearchFiltersToPropertyPath,
  buildPropertyBreadcrumbSegmentsFromArea,
  formatLocationAddressQueryLabel,
} from "~/components/blocks/Geosearch/locationGeosearch";
import { buildSearchPageHref } from "~/lib/helpers/StringHelper";
import { getRegionConfigByCountry } from "custom-config/region.configs";
import { getSpaceTypeCountsFromListing } from "~/lib/helpers/spaceTypeChipHelper";
import {
  buildCardListingDisplayFromListing,
  resolveListingIsFeatured,
  collectFeaturedPropertyIds,
} from "~/lib/helpers/cardListingDisplay";
import { useProgressiveRenderCount } from "~/lib/hooks/useProgressiveRenderCount";
import { isPerformanceLabEnvironment } from "~/lib/hooks/useViewportListener";

/** Blurred static map while Mapbox boots — keeps the pane visually complete. */
function MapPaneSkeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-[#f5f5f5] ${className}`}
      role="status"
      aria-label="Loading map"
      aria-live="polite"
    >
      <img
        src="/assets/map/map-loading-blur.png"
        alt=""
        aria-hidden
        decoding="async"
        fetchPriority="low"
        className="absolute inset-0 h-full w-full object-cover scale-105 blur-sm"
      />
      <div className="absolute inset-0 bg-white/10" aria-hidden />
    </div>
  );
}

/** Survives React remounts within a tab; resets on full page refresh. */
let geosearchDesktopMapReady = false;

const FilterDrawer = lazy(() => loadLazyModule(() => import("./Drawer"), EmptyLazyFallback));
// The map is an enhancement, not the page. `lazy` rethrows a failed dynamic
// import at the nearest error boundary, so when this chunk could not be fetched
// the whole listings page died with it — that is exactly what Googlebot hit
// ("Failed to fetch dynamically imported module: map.client-*.js"), and why
// Search Console rendered an error instead of the results.
// Falling back to nothing keeps the listings intact; only the map is missing.
// `loadLazyModule` also covers the GSC case where Vite *resolves* the import
// to undefined after vite:preloadError preventDefault() (reading 'default').
const CustomMap = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Map/map.client"),
    EmptyLazyFallback,
  ),
);
const EndOfList = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Geosearch/EndOfList"),
    EmptyLazyFallback,
  ),
);



export default function DesktopLayout({
  query,
  workspace,
  filterData,
  callGeoSearch,
  callGeoSearchForDrawer,
  setFilterData,
  data,
  currentPage,
  selectedSort,
  setSelectedSort,
  totalPages,
  handlePageChange,
  renderPaginationItems,
  featuredProperties,
  maxPrice,
  drawerUrl,
  onApplyDrawerFilters,
  parsedQuery,
  isProperty,
  areaSummary,
  hideAreaDesktopLayout = false,
  pageSize,
  isLocationGeosearch = false,
}: {
  query: string
  workspace: Workspace
  data: any
  callGeoSearch: Function
  callGeoSearchForDrawer: Function
  setFilterData: Function
  filterData: any
  currentPage: number
  selectedSort: { label: string, value: string }
  setSelectedSort: Function
  totalPages: number
  handlePageChange: Function
  renderPaginationItems: Function
  featuredProperties: any
  maxPrice: any
  mobile: Boolean
  drawerUrl: string
  onApplyDrawerFilters: VoidFunction
  parsedQuery: Record<string, string>
  isProperty: boolean
  areaSummary: any
  hideAreaDesktopLayout?: boolean
  pageSize: number
  isLocationGeosearch?: boolean
}) {
  const rightContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pageHref = useCallback(
    (page: number) => {
      const path =
        typeof data?.currentLocationPath === "string" && data.currentLocationPath
          ? data.currentLocationPath.split("?")[0]
          : location.pathname;
      const params = new URLSearchParams(searchParams);
      if (
        data?.currentLocationPath &&
        (params.get("location") || "").toLowerCase() === "current"
      ) {
        params.delete("location");
      }
      return buildSearchPageHref(path, params, page);
    },
    [searchParams, location.pathname, data?.currentLocationPath],
  );

  const [displayMode, setDisplayMode] = useState("grid");
  const [isOpen, setIsOpen] = useState(false);
  const [rightWidth, setRightWidth] = useState(45);
  const [hoverIds, setHoverIds] = useState<string[]>([]);

  const [recalibrate, setRecalibrate] = useState(false);
  const [shouldLoadMap, setShouldLoadMap] = useState(() => geosearchDesktopMapReady);
  const [mapRevealed, setMapRevealed] = useState(() => geosearchDesktopMapReady);
  const mapMountedOnceRef = useRef(geosearchDesktopMapReady);
  const [hideSearchBar, setHideSearchBar] = useState(false);
  const isCrawler = Boolean(
    (useRouteLoaderData("root") as { isCrawler?: boolean } | undefined)?.isCrawler,
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ inView?: boolean }>;
      setHideSearchBar(!!ce.detail?.inView);
    };
    window.addEventListener("areaDesktopLayoutInView", handler as EventListener);
    return () => window.removeEventListener("areaDesktopLayoutInView", handler as EventListener);
  }, []);

  // Safely access data with proper fallbacks
  const listData = (data?.data?.data && Array.isArray(data.data.data)) ? data.data.data : [];
  const hasSearchResults = listData.length > 0;
  const mapPinsData = (data?.mapPins && Array.isArray(data.mapPins)) ? data.mapPins : [];
  const nearByData = (data?.data?.autoReCalibratedData && Array.isArray(data.data.autoReCalibratedData)) ? data.data.autoReCalibratedData : [];
  const nearbyPropertyData =
    data?.nearbyPropertyListings?.data && Array.isArray(data.nearbyPropertyListings.data)
      ? data.nearbyPropertyListings.data
      : [];
  const nearByProperties = (data?.nearByProperties && Array.isArray(data.nearByProperties)) ? data.nearByProperties : [];
  const nearbySectionItems = nearbyPropertyData.length > 0 ? nearbyPropertyData : nearByData;
  const showNearbyPropertyCards = nearbyPropertyData.length > 0;
  const hasNearbySection = nearbySectionItems.length > 0;
  const isViewportDrivenSearch = Boolean(
    parsedQuery?.minLatitude &&
    parsedQuery?.maxLatitude &&
    parsedQuery?.minLongitude &&
    parsedQuery?.maxLongitude,
  );
  // Render property data (property cards + property pins) on area pages
  // (`areaSummary`) and on the SEO property pages (`isProperty`); otherwise the
  // search page shows listing cards/pins.
  const showPropertyData = isProperty || !!areaSummary;
  const mapMarkerData = showPropertyData && mapPinsData.length > 0 ? mapPinsData : listData;
  const markerVariant = showPropertyData ? "teal-pin" : "price-pill";

  const listingRenderKey = `${currentPage}-${query}`;

  // Reveal cards in idle batches so hydrating ~30 PGridCards is not one long TBT task.
  // Crawlers get the full set immediately for SEO HTML completeness.
  const visiblePrimaryCount = useProgressiveRenderCount(
    listData.length,
    listingRenderKey,
    {
      initial: isCrawler ? listData.length : 6,
      batchSize: 6,
      timeoutMs: 200,
    },
  );

  const featuredPropertyIds = useMemo(
    () => collectFeaturedPropertyIds(featuredProperties),
    [featuredProperties],
  );

  const resolveCardIsFeatured = useCallback(
    (record: PropertyData) =>
      resolveListingIsFeatured(record, featuredPropertyIds),
    [featuredPropertyIds],
  );

  // Load Mapbox once per full page load/refresh. Filter/location changes must
  // not remount it or flash the blur placeholder. Module flag survives layout remounts.
  useEffect(() => {
    if (geosearchDesktopMapReady || mapMountedOnceRef.current) {
      setShouldLoadMap(true);
      setMapRevealed(true);
      mapMountedOnceRef.current = true;
      return;
    }
    if (isCrawler || isPerformanceLabEnvironment()) {
      setShouldLoadMap(false);
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;

    const markReady = () => {
      if (cancelled) return;
      geosearchDesktopMapReady = true;
      mapMountedOnceRef.current = true;
      setShouldLoadMap(true);
    };

    const scheduleAfterLoad = () => {
      // After `load` + delay so Lighthouse can settle before Mapbox eval.
      timeoutId = window.setTimeout(markReady, 2000);
    };

    if (document.readyState === "complete") {
      scheduleAfterLoad();
    } else {
      window.addEventListener("load", scheduleAfterLoad, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", scheduleAfterLoad);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [isCrawler]);

  // Crossfade: keep blur visible briefly while Mapbox mounts, then fade map in.
  useEffect(() => {
    if (!shouldLoadMap || mapRevealed) return;
    let raf1 = 0;
    let raf2 = 0;
    let timeoutId: number | undefined;
    // Two frames so the map layer paints at opacity 0, then fade.
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => setMapRevealed(true), 350);
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [shouldLoadMap, mapRevealed]);

  const navigation = useNavigation()
  const revalidator = useRevalidator()
  const isSearchLoading =
    navigation.state === "loading" || revalidator.state === "loading"

  const { country, state, city } = data?.params || {}

  const toggleDrawer: VoidFunction = () => {
    setIsOpen((prevState) => !prevState);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1285) {
        setRightWidth(45);
      } else if (window.innerWidth >= 1024) {
        setRightWidth(30);
      } else {
        setRightWidth(0);
      }
    };
    handleResize(); // Set width initially based on screen size
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // CSS auto-fill so columns are correct on first paint (avoids 1-col → multi-col shift
  // while listWidth was still 0 awaiting ResizeObserver / map mount).
  const cardMinWidth = 320;
  const cardsGridStyle = useMemo(
    () => ({
      gridTemplateColumns:
        displayMode === "grid" && rightWidth < 70
          ? `repeat(auto-fill, minmax(${cardMinWidth}px, 1fr))`
          : "1fr",
      position: "relative" as const,
    }),
    [displayMode, rightWidth],
  );


  const addHoverClass = useCallback((id: string | null | undefined, propertyId?: string | null | undefined) => {
    if (!id && !propertyId) {
      setHoverIds([]);
      return;
    }
    const ids = new Set<string>();
    if (id) ids.add(String(id));
    if (propertyId) ids.add(String(propertyId));
    setHoverIds([...ids]);
  }, [])

  const withPropertyDetailFilters = useCallback(
    (propertyPath: string) => {
      if (!isProperty || !propertyPath) return propertyPath;
      return appendGeosearchFiltersToPropertyPath(propertyPath, {
        officeTypes: filterData?.officeTypes,
        minPeople: filterData?.minPeople,
        deskRange: filterData?.deskRange,
        officeTypeSlug: data?.officeTypeSlug,
        parsedQuery,
      });
    },
    [
      isProperty,
      filterData?.officeTypes,
      filterData?.minPeople,
      filterData?.deskRange,
      data?.officeTypeSlug,
      parsedQuery,
    ],
  );

  const renderNearbyListings = useMemo(() => {
    if (!nearbySectionItems.length) return null;

    return nearbySectionItems.map((b: PropertyData, index: number) => {
      if (showNearbyPropertyCards) {
        const link = propertySlug(b as any);
        const propertyLink = propertySlug({ ...b, id: b.id } as any);
        const buildingLink = b?.buildingData ? buildingSlug(b?.buildingData) : "";
        const workspaceLink = b?.workspaceData
          ? workspaceSlug(b.workspaceData as any)
          : "";
        const regionSlug = getRegionConfigByCountry(b.country)?.region ?? "";
        const baseDisplay = {
          images: b.assets ?? [],
          listingId: b.id,
          propertyId: b.propertyId ?? b.id,
          workspaceId: b.workspaceId,
          buildingId: b.buildingId,
          telemetryString: `${b.id}.${b.propertyId ?? b.id}.${b.workspaceId || ""}.${b.buildingId || ""}`,
          webPrimaryKeyword: b.webPrimaryKeyword,
          partnerLogo: b?.workspaceData?.partnerFavicon || b.partnerLogo,
          workspaceProviderName: b.workspaceProviderName,
          showSpecialOfferBadge: !!b.specialOffer,
          offerLabel: b.websiteUniqueOffer || b.specialOffer,
          offerLabelRelaxed: b.websiteUniqueOffer || b.specialOffer,
          buildingTitle: b.buildingName || b.address || b.name || "",
          addressFormula: b.addressFormula || "",
          deskRangeLabel: `${b.minDesks ?? ""}${b.maxDesks ? ` - ${b.maxDesks}` : ""} DESKS`,
          desksBadgeValue: b.maxDesks ?? 0,
          spaceTypeLabel: b.propertyType__c || "",
          spaceTypeCounts: getSpaceTypeCountsFromListing(b),
          showSpaceTypeIcon: true,
          showDesksRow: true,
          closestBusStopName: b.closestBusStopName,
          closestBusStopTravelTime:
            b.closestBusStopTravelTime == null
              ? b.closestBusStopTravelTime
              : Math.round(Number(b.closestBusStopTravelTime)),
          closestTrainStationName: b.closestTrainStationName,
          closestTrainStationTravelTime:
            b.closestTrainStationTravelTime == null
              ? b.closestTrainStationTravelTime
              : Math.round(Number(b.closestTrainStationTravelTime)),
          currencyIsoCode: b.currencyIsoCode,
          avgMonthlyCostPerWorkstation: b.avgMonthlyCostPerWorkstation,
          priceMonthlyMin: b.priceMonthlyMin,
          priceMonthly: b.priceMonthly,
          advertisedPriceMonthly: b.advertisedPriceMonthly,
          showDiscountedListingPrice: false,
          propertyTotalActiveListings: b.propertyTotalActiveListings,
          minDesks: b.minDesks,
          maxDesks: b.maxDesks,
          mohLastModifiedDate: b.mohLastModifiedDate,
          country: b.country,
          compareRegionSlug: regionSlug,
          enquireRegionSlug: regionSlug,
          isFeatured: resolveCardIsFeatured(b),
        } as const;

        const displayGrid = { ...baseDisplay, showPartnerLogo: !b.isPrivateProvider };
        const displayList = { ...baseDisplay, showPartnerLogo: !b.workspaceProvider?.isPrivateSpaceAccount };
        const common = {
          buildingLink,
          workspaceLink,
          onHover: addHoverClass,
          link,
          propertyLink,
          loading: isSearchLoading,
          isFirstVisible: false,
          altText: b.addressFormula,
        };

        const cardComponent =
          displayMode === "grid" ? (
            <PGridCard
              {...common}
              {...displayGrid}
              key={`grid-${b.id}`}
              isPropertyPage={true}
              showFavoriteButton={false}
            />
          ) : (
            <PListCard
              {...common}
              {...displayList}
              key={`list-${b.id}`}
              isPropertyPage={true}
              showFavoriteButton={false}
            />
          );

        return (
          <div key={`card-${b.id}`} className={index > 0 ? "listing-card-deferred min-w-0" : "min-w-0"}>
            {cardComponent}
          </div>
        );
      }

      const link = isProperty
        ? withPropertyDetailFilters(propertySlug(b as any))
        : listingSlug(b as any);
      const nestedProperty =
        b.propertyData && typeof b.propertyData === "object" ? b.propertyData : null;
      const propertyLink = isProperty
        ? withPropertyDetailFilters(propertySlug(b as any))
        : propertySlug({
          ...(nestedProperty || b),
          id: (nestedProperty as any)?.id ?? b.propertyId ?? b.id,
        } as any);
      const buildingLink = b?.buildingData ? buildingSlug(b?.buildingData) : "";
      const workspaceLink = b?.workspaceData
        ? workspaceSlug(b.workspaceData as any)
        : "";
      const regionSlug = getRegionConfigByCountry(b.country)?.region ?? "";
      const baseDisplay = {
        images: b.assets ?? [],
        listingId: b.id,
        // `/property/geo-search` projects `id` but not `propertyId`, and location
        // pages now feed that payload through here as the widened fallback set.
        propertyId: b.propertyId ?? b.id,
        workspaceId: b.workspaceId,
        buildingId: b.buildingId,
        telemetryString: `${b.id}.${b.propertyId ?? b.id}.${b.workspaceId || ''}.${b.buildingId || ''}`,
        webPrimaryKeyword: b.webPrimaryKeyword,
        partnerLogo: b?.workspaceData?.partnerFavicon || b.partnerLogo,
        workspaceProviderName: b.workspaceProviderName,
        showSpecialOfferBadge: !!b.specialOffer,
        offerLabel: b.websiteUniqueOffer || b.specialOffer,
        offerLabelRelaxed: b.websiteUniqueOffer || b.specialOffer,
        buildingTitle: b.buildingName || b.address || b.name || "",
        addressFormula: b.addressFormula || "",
        deskRangeLabel: isProperty ? `${b.minDesks ?? ""}${b.maxDesks ? `-${b.maxDesks}` : ""} DESK` : `${b.noOfWS ?? 0} DESK${(b.noOfWS ?? 0) === 1 ? "" : "S"}`,
        desksBadgeValue: isProperty ? (b.maxDesks ?? 0) : (b.noOfWS ?? 0),
        spaceTypeLabel: isProperty ? (b.propertyType__c || "") : ((b.spaceTypeFormula && b.spaceTypeFormula.toUpperCase()) || "Private Office"),
        spaceTypeCounts: getSpaceTypeCountsFromListing(b),
        showSpaceTypeIcon: true,
        showDesksRow: true,
        closestBusStopName: b.closestBusStopName,
        closestBusStopTravelTime:
          b.closestBusStopTravelTime == null
            ? b.closestBusStopTravelTime
            : Math.round(Number(b.closestBusStopTravelTime)),
        closestTrainStationName: b.closestTrainStationName,
        closestTrainStationTravelTime:
          b.closestTrainStationTravelTime == null
            ? b.closestTrainStationTravelTime
            : Math.round(Number(b.closestTrainStationTravelTime)),
        currencyIsoCode: b.currencyIsoCode,
        avgMonthlyCostPerWorkstation: b.avgMonthlyCostPerWorkstation,
        priceMonthlyMin: b.priceMonthlyMin,
        priceMonthly: b.priceMonthly,
        advertisedPriceMonthly: b.advertisedPriceMonthly,
        showDiscountedListingPrice: !!b.specialOffer && (b.advertisedPriceMonthly < b.priceMonthly),
        propertyTotalActiveListings: b.propertyTotalActiveListings,
        minDesks: isProperty ? b.minDesks : undefined,
        maxDesks: isProperty ? b.maxDesks : b.noOfWS,
        mohLastModifiedDate: b.mohLastModifiedDate,
        country: b.country,
        compareRegionSlug: regionSlug,
        enquireRegionSlug: regionSlug,
        isFeatured: resolveCardIsFeatured(b),
        showSpaceTypeChips: true
      } as const;

      const displayGrid = { ...baseDisplay, showPartnerLogo: !b.isPrivateProvider };
      const displayList = { ...baseDisplay, showPartnerLogo: !b.workspaceProvider?.isPrivateSpaceAccount };
      const common = {
        buildingLink,
        workspaceLink,
        onHover: addHoverClass,
        link,
        propertyLink,
        loading: isSearchLoading,
        isFirstVisible: false,
        altText: b.addressFormula,
      };

      const cardComponent = displayMode === 'grid'
        ? <PGridCard {...common} {...displayGrid} key={`grid-${b.id}`} isPropertyPage={isProperty} />
        :
        <PListCard {...common} {...displayList} key={`list-${b.id}`} isPropertyPage={isProperty} />;

      return (
        <div key={`card-${b.id}`} className={index > 0 ? "listing-card-deferred min-w-0" : "min-w-0"}>
          {cardComponent}
        </div>
      );
    });
  }, [
    nearbySectionItems,
    showNearbyPropertyCards,
    displayMode,
    navigation.state,
    isProperty,
    withPropertyDetailFilters,
    addHoverClass,
    resolveCardIsFeatured,
  ]);

  const renderCards = useMemo(() => {

    if (data?.data?.data && Array.isArray(data.data.data) && data.data.data.length) {
      let cards = data.data.data
        .slice(0, visiblePrimaryCount)
        .map((b: PropertyData, index: number) => {
          const link = isProperty
            ? withPropertyDetailFilters(propertySlug(b as any))
            : listingSlug(b as any);
          const propertyLinkBase = propertySlug({
            ...b,
            id: isProperty ? b.id : b.propertyId,
          } as any);
          const propertyLink = isProperty
            ? withPropertyDetailFilters(propertyLinkBase)
            : propertyLinkBase;
          const buildingLink = b?.buildingData ? buildingSlug(b?.buildingData) : "";
          const workspaceLink = b?.workspaceData
            ? workspaceSlug(b?.workspaceData as any)
            : "";
          const regionSlug = getRegionConfigByCountry(b.country)?.region ?? "";
          console.log(b);
          const baseDisplay = {
            images: b.assets ?? [],
            listingId: b.id,
            propertyId: b.propertyId,
            workspaceId: b.workspaceId,
            buildingId: b.buildingId,
            telemetryString: `${b.id}.${b.propertyId}.${b.workspaceId || ''}.${b.buildingId || ''}`,
            webPrimaryKeyword: b.webPrimaryKeyword,
            partnerLogo: b?.workspaceData?.partnerFavicon || b.partnerLogo,
            workspaceProviderName: b.workspaceProviderName,
            showSpecialOfferBadge: !!b.specialOffer,
            offerLabel: b.websiteUniqueOffer || b.specialOffer,
            offerLabelRelaxed: b.websiteUniqueOffer || b.specialOffer,
            buildingTitle: b.buildingName || b.address || b.name || "",
            addressFormula: b.addressFormula || "",
            deskRangeLabel: isProperty ? `${b.minDesks ?? ""}${b.maxDesks ? ` - ${b.maxDesks}` : ""} DESKS` : `${b.noOfWS ?? 0} DESK${(b.noOfWS ?? 0) === 1 ? "" : "S"}`,
            desksBadgeValue: isProperty ? (b.maxDesks ?? 0) : (b.noOfWS ?? 0),
            spaceTypeLabel: isProperty ? (b.propertyType__c || "") : ((b.spaceTypeFormula && b.spaceTypeFormula.toUpperCase()) || "Private Office"),
            spaceTypeCounts: getSpaceTypeCountsFromListing(b),
            showSpaceTypeIcon: true,
            showDesksRow: true,
            closestBusStopName: b.closestBusStopName,
            closestBusStopTravelTime:
              b.closestBusStopTravelTime == null
                ? b.closestBusStopTravelTime
                : Math.round(Number(b.closestBusStopTravelTime)),
            closestTrainStationName: b.closestTrainStationName,
            closestTrainStationTravelTime:
              b.closestTrainStationTravelTime == null
                ? b.closestTrainStationTravelTime
                : Math.round(Number(b.closestTrainStationTravelTime)),
            currencyIsoCode: b.currencyIsoCode,
            avgMonthlyCostPerWorkstation: b.avgMonthlyCostPerWorkstation,
            priceMonthlyMin: b.priceMonthlyMin,
            priceMonthly: b.priceMonthly,
            advertisedPriceMonthly: b.advertisedPriceMonthly,
            showDiscountedListingPrice: !!b.specialOffer && (b.advertisedPriceMonthly < b.priceMonthly),
            propertyTotalActiveListings: b.propertyTotalActiveListings,
            minDesks: isProperty ? b.minDesks : undefined,
            maxDesks: isProperty ? b.maxDesks : b.noOfWS,
            mohLastModifiedDate: b.mohLastModifiedDate,
            country: b.country,
            compareRegionSlug: regionSlug,
            enquireRegionSlug: regionSlug,
            isFeatured: resolveCardIsFeatured(b),
            showSpaceTypeChips: true
          } as const;

          const displayGrid = { ...baseDisplay, showPartnerLogo: !b.isPrivateProvider };
          const displayList = { ...baseDisplay, showPartnerLogo: !b.workspaceProvider?.isPrivateSpaceAccount };

          const common = {
            buildingLink,
            workspaceLink,
            onHover: addHoverClass,
            link,
            propertyLink: propertyLink,
            loading: isSearchLoading,
            isFirstVisible: index === 0,
            altText: b.addressFormula,
            titleText: `${b.address || b.addressFormula}, ${b.noOfWS || b.maxAvailableDesks} ${b.noOfWS === 1 || b.maxAvailableDesks === 1 ? 'DESK' : 'DESKS'}`,
          };

          const cardComponent = displayMode === 'grid'
            ? <PGridCard {...common} {...displayGrid} key={`grid-${b.id}`} isPropertyPage={isProperty} showFavoriteButton={isProperty ? false : true} />
            :
            <PListCard {...common} {...displayList} key={`list-${b.id}`} isPropertyPage={isProperty} showFavoriteButton={isProperty ? false : true} />


          return (
            <div key={`card-${b.id}`} className={index > 0 ? "listing-card-deferred min-w-0" : "min-w-0"}>
              {cardComponent}
            </div>
          );
        });

      return cards;
    } else {
      return (
        <>
          <div className="col-span-full w-full mb-4">
            <NoResults
              query={query}
              params={data.params}
              parsedQuery={parsedQuery}
              callGeoSearch={callGeoSearch as any}
              areaSummary={areaSummary}
              isPropertyPage={isProperty}
              locationBasePath={data?.locationBasePath}
              locationLevel={data?.level}
            />
          </div>

        </>)
    }
  }, [data, displayMode, navigation.state, isProperty, withPropertyDetailFilters, resolveCardIsFeatured, visiblePrimaryCount]);

  const renderNearByFeaturedProperties = useCallback((featuredProperties: any[]) => {
    if (featuredProperties && featuredProperties?.length && currentPage === 1 && !(query.includes('minPrice') || query.includes('maxPrice') || query.includes('minPeople') || query.includes('maxPeople') || query.includes('Amenities') || query.includes('Facilities') || query.includes('sale'))) {
      let cards = featuredProperties.map((b: any, index: number) => {
        const propertyLinkBase = propertySlug({
          ...b
        } as any);
        const propertyLink = isProperty
          ? withPropertyDetailFilters(propertyLinkBase)
          : propertyLinkBase;
        const buildingLink = b?.buildingData ? buildingSlug(b?.buildingData) : "";
        const workspaceLink = b?.workspaceData
          ? workspaceSlug(b.workspaceData as any)
          : "";
        const cardComponent = displayMode === "grid" && isProperty
          ? (
            <PGridCard
              buildingLink={buildingLink}
              workspaceLink={workspaceLink}
              link={propertyLink}
              propertyLink={propertyLink}
              loading={isSearchLoading}
              isFirstVisible={false}
              altText={b.addressFormula}
              isPropertyPage={true}
              showFavoriteButton={false}
              {...{
                ...buildCardListingDisplayFromListing(b, { isProperty: true }),
                showPartnerLogo:
                  !b.isPrivateProvider &&
                  !b.workspaceProvider?.isPrivateSpaceAccount,
                isFeatured: true,
              }}
              showSpaceTypeChips={true}
            />
          )
          : (
            <PListCard
              buildingLink={buildingLink}
              workspaceLink={workspaceLink}
              link={propertyLink}
              propertyLink={propertyLink}
              loading={isSearchLoading}
              isFirstVisible={false}
              altText={b.addressFormula}
              isPropertyPage={true}
              showFavoriteButton={false}
              {...{
                ...buildCardListingDisplayFromListing(b, { isProperty: true }),
                showPartnerLogo:
                  !b.isPrivateProvider &&
                  !b.workspaceProvider?.isPrivateSpaceAccount,
                isFeatured: true,
              }}
            />
          );

        return (
          <div key={`card-${b.id}`} className={index > 0 ? "listing-card-deferred min-w-0" : "min-w-0"}>
            {cardComponent}
          </div>
        );
      });

      return cards;
    }
    return null;
  }, [currentPage, query, navigation.state, isProperty, withPropertyDetailFilters, displayMode]);

  const areaHeadlineBrowseContext = useMemo(
    () =>
      areaSummary
        ? getAreaHeadlineBrowseContext(searchParams, parsedQuery)
        : {
          firstPageLoad: parsedQuery?.firstPageLoad,
          workspaceType: parsedQuery?.workspaceType,
          hasLocationQueryParams: false,
        },
    [areaSummary, searchParams, parsedQuery],
  );

  const headlineSpaceType = useMemo(
    () =>
      resolveAreaPageHeadlineSpaceType({
        selectedOfficeTypes: filterData?.officeTypes,
        areaSpaceTypeFormula: data?.areaSummary?.spaceTypeFormula,
        areaSpaceType: data?.areaSummary?.spaceType,
        officeTypeFilterTouched: parsedQuery?.officeTypeFilterTouched === "1",
        workspaceType: areaHeadlineBrowseContext.workspaceType,
        firstPageLoad: areaHeadlineBrowseContext.firstPageLoad,
        hasLocationQueryParams: areaHeadlineBrowseContext.hasLocationQueryParams,
      }),
    [
      filterData?.officeTypes,
      data?.areaSummary?.spaceTypeFormula,
      data?.areaSummary?.spaceType,
      parsedQuery?.officeTypeFilterTouched,
      areaHeadlineBrowseContext,
    ],
  );

  const officeTitle = useMemo(() => {
    const summary = data?.areaSummary;
    const paramCity = parsedQuery?.city ? String(parsedQuery.city) : "";
    const paramState = parsedQuery?.state ? String(parsedQuery.state) : "";
    const paramCountry = parsedQuery?.country
      ? String(parsedQuery.country).toLowerCase()
      : "";
    const hasAnyLocationParams = Boolean(
      paramCountry || paramState || paramCity,
    );
    const addressQuery = formatLocationAddressQueryLabel(
      searchParams.get("q") || parsedQuery?.q,
    );

    return buildAreaPageListingHeadline({
      spaceTypeFormula: headlineSpaceType,
      areaSummary: summary,
      paramCity,
      paramState,
      paramCountry,
      areaCity: summary?.city || summary?.suburb || city,
      areaState: summary?.state || state,
      areaCountry: summary?.country || country,
      routeCountry: country,
      hasAnyLocationParams,
      placeOverride: isLocationGeosearch
        ? data?.locationText?.trim() || undefined
        : undefined,
      addressQuery: addressQuery || undefined,
    });
  }, [
    data?.areaSummary,
    data?.locationText,
    country,
    state,
    city,
    parsedQuery?.country,
    parsedQuery?.state,
    parsedQuery?.city,
    parsedQuery?.q,
    headlineSpaceType,
    isLocationGeosearch,
    searchParams,
  ]);

  const officeSubheadline = useMemo(() => {
    const summary = data?.areaSummary ?? areaSummary;
    const listings =
      data?.data?.data && Array.isArray(data.data.data) ? data.data.data : [];
    const fallbackListings = nearbyPropertyData.length > 0
      ? nearbyPropertyData
      : (data?.data?.autoReCalibratedData &&
        Array.isArray(data.data.autoReCalibratedData)
        ? data.data.autoReCalibratedData
        : []);
    const total =
      data?.data?.totalProperties ?? data?.data?.totalListings ?? 0;
    const paramCity = parsedQuery?.city ? String(parsedQuery.city) : "";
    const paramState = parsedQuery?.state ? String(parsedQuery.state) : "";
    const paramCountry = parsedQuery?.country
      ? String(parsedQuery.country).toLowerCase()
      : "";
    const hasAnyLocationParams = Boolean(
      paramCountry || paramState || paramCity || city || state || country,
    );
    const regionCurrency =
      data?.regionData?.currencyIsoCode ?? data?.regionData?.currency;

    if (isLocationGeosearch || !summary) {
      if (isLocationGeosearch) {
        return buildLocationGeosearchPageListingSubheadline({
          data,
          parsedQuery,
          filterData,
        });
      }
      return buildGeoSearchPageListingSubheadline({
        data,
        parsedQuery,
        filterData,
      });
    }

    return buildAreaPageListingSubheadline({
      areaSummary: summary,
      totalProperties: total,
      listings,
      fallbackListings,
      regionCurrency,
      paramCity,
      paramState,
      paramCountry,
      areaCity: summary?.city || summary?.suburb || city,
      areaState: summary?.state || state,
      areaCountry: summary?.country || country,
      routeCountry: country,
      hasAnyLocationParams,
      selectedOfficeTypes: filterData?.officeTypes,
      areaSpaceTypeFormula: data?.areaSummary?.spaceTypeFormula,
      areaSpaceType: data?.areaSummary?.spaceType,
      officeTypeFilterTouched: parsedQuery?.officeTypeFilterTouched === "1",
      workspaceType: areaHeadlineBrowseContext.workspaceType,
      firstPageLoad: areaHeadlineBrowseContext.firstPageLoad,
      hasLocationQueryParams: areaHeadlineBrowseContext.hasLocationQueryParams,
      globalPriceTier: data?.globalPriceTier,
      mapPins: data?.mapPins,
    });
  }, [
    areaSummary,
    data,
    filterData,
    data?.areaSummary,
    data?.areaSummary?.spaceTypeFormula,
    data?.areaSummary?.spaceType,
    data?.data?.data,
    nearbyPropertyData,
    data?.data?.autoReCalibratedData,
    data?.data?.totalListings,
    data?.data?.totalProperties,
    data?.regionData?.currencyIsoCode,
    data?.regionData?.currency,
    data?.globalPriceTier,
    data?.mapPins,
    parsedQuery?.city,
    parsedQuery?.state,
    parsedQuery?.country,
    parsedQuery?.officeTypeFilterTouched,
    areaHeadlineBrowseContext,
    city,
    state,
    country,
    isLocationGeosearch,
  ]);

  const resultText = useMemo(() => {
    const listings =
      data?.data?.data && Array.isArray(data.data.data) ? data.data.data : [];
    const total = data?.data?.totalProperties ?? data?.data?.totalListings ?? 0;

    if (!listings.length) return null;

    const start = (currentPage - 1) * pageSize + 1;
    const end = start + listings.length - 1;

    if (!areaSummary) {
      return `Showing ${start} - ${end} of ${total.toLocaleString()} spaces`;
    }

    const summary = data?.areaSummary ?? areaSummary;
    const paramCity = parsedQuery?.city ? String(parsedQuery.city) : "";
    const paramState = parsedQuery?.state ? String(parsedQuery.state) : "";
    const paramCountry = parsedQuery?.country
      ? String(parsedQuery.country).toLowerCase()
      : "";
    const hasAnyLocationParams = Boolean(
      paramCountry || paramState || paramCity,
    );

    return buildAreaPageListingResultText({
      start,
      end,
      total,
      spaceTypeFormula: headlineSpaceType,
      areaSummary: summary,
      paramCity,
      paramState,
      paramCountry,
      areaCity: summary?.city || summary?.suburb || city,
      areaState: summary?.state || state,
      areaCountry: summary?.country || country,
      routeCountry: country,
      hasAnyLocationParams,
    });
  }, [
    currentPage,
    pageSize,
    areaSummary,
    headlineSpaceType,
    data?.areaSummary,
    data.data.data,
    data.data.totalListings,
    data.data.totalProperties,
    parsedQuery?.city,
    parsedQuery?.state,
    parsedQuery?.country,
    city,
    state,
    country,
  ]);

  const nearbySectionCity = useMemo(() => {
    if (parsedQuery?.location) {
      const location = decodeURIComponent(String(parsedQuery.location)).trim();
      const cityPart = location.split(",")[0]?.trim();
      if (cityPart) return cityPart;
    }
    const citySlug = parsedQuery?.city ? String(parsedQuery.city) : city ? String(city) : "";
    if (citySlug) {
      const decoded = decodeURIComponent(citySlug);
      return decoded.charAt(0).toUpperCase() + decoded.slice(1).replace(/-/g, " ");
    }
    if (areaSummary?.city || areaSummary?.suburb) {
      return areaSummary.city || areaSummary.suburb;
    }
    const fromLocationText = data?.locationText?.split(",")[0]?.trim();
    return fromLocationText || "your area";
  }, [
    parsedQuery?.location,
    parsedQuery?.city,
    city,
    areaSummary?.city,
    areaSummary?.suburb,
    data?.locationText,
  ]);

  return (
    <Container htmlFor="fluid">
      <div>
        {!hideSearchBar && (
          <SearchBar
            className="top-[70px]"
            toggleDrawer={toggleDrawer}
            setWidth={setRightWidth}
            rightWidth={rightWidth}
            setDisplayMode={setDisplayMode}
            filterData={filterData}
            callGeoSearch={callGeoSearch}
            setFilterData={setFilterData}
            query={query}
            workspace={workspace}
            displayMode={displayMode}
            recalibrate={recalibrate}
            setRecalibrate={setRecalibrate}
            maxPrice={maxPrice}
            areaSummary={areaSummary}
            isPropertyPage={isProperty}
          />
        )}
        {isOpen ? (
          <Suspense fallback={null}>
            <FilterDrawer
              open={isOpen}
              setOpen={setIsOpen}
              data={data}
              filterData={filterData}
              callGeoSearch={callGeoSearchForDrawer}
              workspace={data.workspace}
              url={drawerUrl}
              maxPrice={maxPrice}
              onApply={onApplyDrawerFilters}
              areaSummary={areaSummary}
              isPropertyPage={isProperty}
            />
          </Suspense>
        ) : null}
        <div
          className="flex w-full min-w-0 items-start"
          style={{
            width: '100%',
            paddingTop: hideSearchBar ? 0 : 64,
            minHeight: `calc(100vh - ${hideSearchBar ? 70 : 134}px)`,
          }}
        >
          {/* Left Section */}
          <div
            id={PAGE_SECTION_IDS.searchResults}
            className={`transition-all duration-300 pb-2 px-[14px] pt-4 min-h-[calc(100vh-134px)] min-w-0 w-full ${PAGE_SECTION_SCROLL_CLASS}`}
            style={{
              width: `${100 - rightWidth}%`,
              display: rightWidth === 100 ? 'none' : 'block',
            }}
          >
            <div className="pl-[10px]">
              {areaSummary ? (
                <Breadcrumbs
                  params={data.params}
                  countryRequired={true}
                  isPropertyPage={true}
                  segments={buildPropertyBreadcrumbSegmentsFromArea(areaSummary)}
                  useCurrentPathForLastSegment
                />
              ) : (
                <Breadcrumbs params={data.params} countryRequired={true} isPropertyPage={isProperty} />
              )}

            </div>

            <div className="pl-[10px]">
              <div className='flex flex-col min-w-0 overflow-hidden gap-1'>
                {hasSearchResults && <h1 className='text-3xl font-semibold'>{officeTitle}</h1>}
                {officeSubheadline && hasSearchResults ? (
                  <span className='text-sm font-normal text-oh-dark-gray inline-flex items-center gap-0.5'>
                    {officeSubheadline}
                  </span>
                ) : null}
                <div className="flex items-end justify-between gap-4 min-w-0">
                  {resultText ? (
                    <Text className="text-sm font-medium text-oh-dark-gray">{resultText}</Text>
                  ) : null}
                  {hasSearchResults ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2 focus-visible:shadow-none shrink-0">
                          <span>Sort by: {selectedSort.label}</span>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="z-[20] bg-white p-3 w-64 text-sm rounded-lg border mt-2 shadow-lg">
                        {filterOptions.map((option) => (
                          <DropdownMenuCheckboxItem
                            key={option.value}
                            style={{
                              fontWeight:
                                filterData?.sortBy === option.value &&
                                  filterData?.order === option.order
                                  ? "bold"
                                  : "initial",
                            }}
                            className="hover:bg-gray-100 hover:font-bold"
                            checked={
                              filterData?.sortBy === option.value &&
                              filterData?.order === option.order
                            }
                            onCheckedChange={() => {
                              setFilterData({
                                ...filterData,
                                sortBy: option.value,
                                order: option.order,
                              });
                              setSelectedSort(option);
                              callGeoSearch({
                                sortBy: option.value,
                                order: option.order,
                              });
                            }}
                          >
                            {option.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}

                </div>
              </div>
            </div>
            <div className="pt-2">
              {!hasSearchResults ? (
                <div className="col-span-full w-full mb-4">
                  <NoResults
                    query={query}
                    params={data.params}
                    parsedQuery={parsedQuery}
                    callGeoSearch={callGeoSearch as any}
                    areaSummary={areaSummary}
                    isPropertyPage={isProperty}
                    locationBasePath={data?.locationBasePath}
                    locationLevel={data?.level}
                  />
                </div>
              ) : (
                <>
                  {data?.data?.data && Array.isArray(data.data.data) && data.data.data.length ? <div className="grid gap-3 mb-2 w-full">
                    {renderNearByFeaturedProperties(featuredProperties || [])}
                  </div> : null}
                  <div
                    className="grid gap-3"
                    style={cardsGridStyle}
                  >
                    {renderCards}
                  </div>
                </>
              )}
            </div>
            {currentPage > 1 && currentPage === totalPages ? <div className="my-2">
              <Suspense fallback={null}>
                <EndOfList
                  query={data.query}
                  params={data.params}
                  parsedQuery={parsedQuery}
                  callGeoSearch={callGeoSearch as any}
                  areaSummary={areaSummary}
                  isPropertyPage={isProperty}
                  locationBasePath={data?.locationBasePath}
                  locationLevel={data?.level}
                />
              </Suspense>
            </div> : null}
            {hasNearbySection ? (
              <div className="pt-2 ">
                <h2 className="font-semibold mb-2 text-lg">
                  {showNearbyPropertyCards
                    ? `More options in ${nearbySectionCity}`
                    : "More picks from further away"}
                </h2>
                <div
                  className="grid gap-2 "
                  style={cardsGridStyle}
                >
                  {renderNearbyListings}
                </div>
              </div>
            ) : null}
            {
              (totalPages > 1 && data?.data?.data && Array.isArray(data.data.data) && data.data.data.length > 0) && (
                <div className="flex justify-center items-center w-full my-4">
                  <Pagination className="justify-center w-full" >
                    <PaginationContent>
                      <PaginationItem className="cursor-pointer">
                        <PaginationPrevious
                          to={currentPage > 1 ? pageHref(currentPage - 1) : ""}
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                      </PaginationItem>


                      {renderPaginationItems()}

                      <PaginationItem className="cursor-pointer">
                        <PaginationNext
                          to={currentPage < totalPages ? pageHref(currentPage + 1) : ""}
                          disabled={currentPage >= totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </PaginationItem>

                    </PaginationContent>

                  </Pagination>
                </div>
              )
            }
          </div>

          {/* Right Section (Resizable) — blur placeholder crossfades into Mapbox */}
          <div
            id={PAGE_SECTION_IDS.map}
            ref={rightContainerRef}
            className={`transition-all duration-300 z-[0] hidden min-w-0 overflow-hidden lg:block self-start ${PAGE_SECTION_SCROLL_CLASS}`}
            style={{
              width: `${rightWidth}%`,
              position: 'sticky',
              top: hideSearchBar ? 70 : 134,
              height: `calc(100vh - ${hideSearchBar ? 70 : 134}px)`,
            }}
          >
            <div className="relative h-full w-full">
              <div
                className={`absolute inset-0 z-0 transition-opacity duration-700 ease-out ${mapRevealed ? "opacity-0 pointer-events-none" : "opacity-100"
                  }`}
                aria-hidden={mapRevealed}
              >
                <MapPaneSkeleton />
              </div>
              {shouldLoadMap ? (
                <div
                  className={`absolute inset-0 z-10 transition-opacity duration-700 ease-out ${mapRevealed ? "opacity-100" : "opacity-0"
                    }`}
                >
                  <Suspense fallback={null}>
                    <ClientOnly fallback={null}>
                      {() => (
                        <CustomMap
                          data={mapMarkerData}
                          totalListings={data?.data?.totalProperties ?? data?.data?.totalListings ?? 0}
                          rightWidth={rightWidth}
                          rightContainerRef={rightContainerRef}
                          width={rightWidth}
                          setWidth={setRightWidth}
                          loading={isSearchLoading}
                          currentPage={currentPage}
                          handlePageChange={handlePageChange}
                          callGeoSearch={callGeoSearch}
                          displayMode={displayMode}
                          nearbyListings={showNearbyPropertyCards ? nearbyPropertyData : nearByData}
                          nearByProperties={isViewportDrivenSearch ? nearByProperties : []}
                          mobile={false}
                          query={query}
                          hoverIds={hoverIds}
                          onHover={addHoverClass}
                          region={country}
                          recalibrate={recalibrate}
                          setRecalibrate={setRecalibrate}
                          parsedQuery={parsedQuery}
                          locationPolygon={data?.locationPolygon}
                          locationPolygonBounds={data?.locationPolygonBounds}
                          globalPriceTier={data?.globalPriceTier ?? null}
                          showPanelControl={true}
                          isPropertyCard={showPropertyData || nearbyPropertyData.length > 0}
                          featuredPropertyIds={featuredPropertyIds}
                          showPerDesk={showPropertyData || nearbyPropertyData.length > 0}
                          markerVariant={markerVariant}
                          isAreaPage={!!areaSummary}
                          pageSize={pageSize}
                        />
                      )}
                    </ClientOnly>
                  </Suspense>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {areaSummary && !hideAreaDesktopLayout ?
          <div className="area-seo-below-fold">
            <AreaDesktopLayout
              data={data}
              areaSummary={areaSummary}
              faqs={data.faqs}
              nearbyOffices={data.nearbyOffices}
              trends={areaSummary?.areaProfileTrends}
              areaBlogs={data.areaBlogs}
              partners={data.partners}
              managers={data.managers}
              region={data.region}
            />
          </div>
          : null}
      </div >
    </Container >
  );
}
